import {createRandomString, eraseCookie, getCookie, gtag, setCookie, viewTransition} from './utils.js';

/**
 * @typedef {{key: string, forced: boolean, additionalConsentKeys: string[]}} StorageEntry
 */

/**
 * @typedef {{header: HTMLButtonElement, panel: HTMLDivElement}} Tab
 */

/**
 * @enum {string}
 */
const Layout = {
    Default: 'default',
    Popup: 'popup'
};

/** @type {HTMLScriptElement} */
const settingsContainer = document.querySelector('#c3cookie-settings');
/** @type {{host: string, path: string, base: string}} */
const settingsData = JSON.parse(settingsContainer.innerText);

/** @type {{icons: Record<string, string>}} */
const assets = JSON.parse(document.querySelector('#c3cookie-assets').innerText);

const storages = {
    /** @type {string[]} */
    all: [],
    /** @type {string[]} */
    forced: [],
    /** @type {Map<string, StorageEntry>} */
    data: new Map(),
    /** @type {Set<string>} */
    loaded: new Set()
};

{
    /** @type {HTMLScriptElement} */
    const dataContainer = document.querySelector('#c3cookie-storages');

    /** @type {StorageEntry[]} */
    const storagesData = JSON.parse(dataContainer.innerText);
    for (const storage of storagesData) {
        storages.data.set(storage.key, storage);
        storages.all.push(storage.key);
        if (storage.forced) {
            storages.forced.push(storage.key);
        }
    }
}

{
    // migrate old cookie value to new one.
    const cookieValue = getCookie('c3kie');
    if (cookieValue) {
        if (cookieValue.startsWith('save-settings')) {
            eraseCookie('c3kie');
        }
        if (cookieValue.startsWith('accept-all')) {
            eraseCookie('c3kie');
            selectStorages(storages.all);
        }
        if (cookieValue.startsWith('revoke') || cookieValue.startsWith('notice')) {
            eraseCookie('c3kie');
            selectStorages(storages.forced);
        }
    }
}

{
    const defaultGtagData = {};
    for (const storage of storages.all) {
        const keys = [`${storage}_storage`, ...storages.data.get(storage).additionalConsentKeys];
        for (const key of keys) {
            defaultGtagData[key] = 'denied';
        }
    }

    gtag('consent', 'default', defaultGtagData);
}


/** @type {HTMLTemplateElement} */
const baseTemplate = document.querySelector('#c3cookie-template');

/** @type {HTMLDivElement} */
const template = baseTemplate.cloneNode(true).content.firstElementChild;

const buttons = {
    /** @type {HTMLButtonElement} */
    acceptAll: template.querySelector('.c3cookie-button[data-action="accept-all"]'),
    /** @type {HTMLButtonElement} */
    manage: template.querySelector('.c3cookie-button[data-action="manage"]'),
    /** @type {HTMLButtonElement} */
    denyAll: template.querySelector('.c3cookie-button[data-action="deny-all"]'),
    /** @type {HTMLButtonElement} */
    showPurposes: template.querySelector('.c3cookie-button[data-action="show-purposes"]'),
};

const elements = {
    /** @type {HTMLFormElement} */
    form: template.querySelector('#c3cookie-form'),
    /** @type {HTMLDialogElement} */
    dialog: template.querySelector('#c3cookie-dialog'),
    /** @type {HTMLButtonElement|null} */
    flag: template.querySelector('#c3cookie-flag'),
};

const pages = {
    /** @type {HTMLDivElement|null} */
    info: template.querySelector('.c3cookie-page[data-page="info"]'),
    /** @type {HTMLDivElement} */
    settings: template.querySelector('.c3cookie-page[data-page="settings"]'),
    /** @type {HTMLDivElement} */
    purposes: template.querySelector('.c3cookie-page[data-page="purposes"]'),
};

/**
 * @const
 * @type {HTMLDivElement[]}
 */
const pagesWithMobileBackdrop = [pages.settings, pages.purposes];

const classes = {
    pageActive: 'c3cookie-page--active'
};

/** @type {Tab[]} */
const purposeTabs = [];

/** @type {NodeListOf<HTMLDivElement>} */
const tabPanels = template.querySelectorAll('.c3cookie-tab-panel');
for (const panel of tabPanels) {
    /** @type {HTMLButtonElement} */
    const header = template.querySelector(`#${panel.getAttribute('aria-labelledby')}`);

    /** @type {Tab} */
    const tab = {header, panel};
    purposeTabs.push(tab);

    header.addEventListener('click', () => {
        viewTransition(() => {
            selectPurposeTab(tab);
        });
    });

    header.addEventListener('keydown', e => {
        if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && header.nextElementSibling) {
            header.tabIndex = -1;
            header.nextElementSibling.tabIndex = 0;
            header.nextElementSibling.focus();
        }

        if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && header.previousElementSibling) {
            header.tabIndex = -1;
            header.previousElementSibling.tabIndex = 0;
            header.previousElementSibling.focus();
        }
    });
}

if (purposeTabs.length) {
    selectPurposeTab(purposeTabs[0]);
}

{
    /** @type {NodeListOf<HTMLButtonElement>} */
    const backButtons = template.querySelectorAll('.c3cookie-back-btn');
    for (const backButton of backButtons) {
        backButton.addEventListener('click', () => {
            const target = backButton.dataset.target;
            if (target in pages) {
                goToPage(pages[target]);
            }
        });
    }
}

buttons.acceptAll?.addEventListener('click', () => {
    selectStorages(storages.all);
    submitCookieSettings().catch(console.error);
    loadCookieScripts().catch(console.error);
    closeCookieWindow();
});

buttons.denyAll?.addEventListener('click', () => {
    selectStorages(storages.forced);
    submitCookieSettings().catch(console.error);

    // only reload, if the user has some configurable cookie scripts loaded
    if (storages.loaded.size > storages.forced.length) {
        location.reload();
        return;
    }

    closeCookieWindow();
});

elements.form.addEventListener('submit', e => {
    e.preventDefault();

    const alreadyLoadedStorages = [...storages.loaded.values()];

    const data = new FormData(elements.form);
    selectStorages([...data.getAll('storages'), ...storages.forced]);
    submitCookieSettings().catch(console.error);
    loadCookieScripts().catch(console.error);

    const newSelectedStorages = getSelectedStorages();

    // check if the newly selected storages fully contain the previously selected storages. Reload if a storage was
    // removed.
    if (!alreadyLoadedStorages.every(storage => newSelectedStorages.includes(storage))) {
        location.reload();
        return;
    }

    closeCookieWindow();
});

buttons.showPurposes.addEventListener('click', () => {
    goToPage(pages.purposes);
});

buttons.manage?.addEventListener('click', () => {
    goToPage(pages.settings);
});

elements.flag?.addEventListener('click', () => {
    openCookieWindow();
});

elements.dialog.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
        e.preventDefault();
    }
});

elements.dialog.addEventListener('cancel', e => {
    e.preventDefault();
});

if (template.dataset.layout === Layout.Popup) {
    pages.settings.classList.add(classes.pageActive);
} else {
    pages.info.classList.add(classes.pageActive);
}

updateCheckboxes();

viewTransition(() => {
    // append the window to the top of the page if the user has not interacted yet, so users with keyboard navigation
    // don't have to tab through the whole page to access the cookie window
    if (!checkCookieLifetime()) {
        document.body.prepend(template);
        openCookieWindow();
    } else {
        document.body.append(template);
    }
})
    .finished
    .then(() => {
        loadCookieScripts().catch(console.error);
        analyze().catch(console.error);
    });

initVideoElements();

/** @type {HTMLTemplateElement|null} */
const privacyTemplate = document.querySelector('#c3cookie-privacy-template');
if (privacyTemplate) {
    const cloned = privacyTemplate.cloneNode(true).content.firstElementChild;
    const target = document.querySelector('.c3cookie-privacy-container')
        ?? document.querySelector('#main')
        ?? document.body;

    cloned.querySelector('button')
        .addEventListener('click', () => {
            openCookieWindow(pages.settings);
        });

    target.append(cloned);
}

customElements.define('c3cookie-icon', class extends HTMLElement {
    constructor() {
        super();
        this.icon = this.getAttribute('icon');
        this.src = `https://${settingsData.host}${assets.icons[this.icon]}`;
        this.content = fetch(this.src)
            .then(res => {
                if (!res.ok || res.status >= 400) {
                    return Promise.resolve(null);
                }
                return res.text();
            })
            .catch(() => null);
        this.setAttribute('aria-hidden', 'true');
    }

    connectedCallback() {
        this.content.then(res => {
            this.innerHTML = res;
        });
    }
});

function openCookieWindow(openedPage = pages.info ?? pages.settings) {
    goToPage(openedPage);
    viewTransition(() => {
        if (elements.flag) {
            elements.flag.hidden = true;
        }

        if (template.dataset.layout === Layout.Popup) {
            elements.dialog.showModal();
            elements.dialog.dataset.modal = 'true';
        } else {
            elements.dialog.show();
            elements.dialog.dataset.modal = 'false';
        }
    });
}

function closeCookieWindow() {
    viewTransition(() => {
        if (elements.flag) {
            elements.flag.hidden = false;
        }

        elements.dialog.close();
    });
}

/**
 * @param {HTMLDivElement} page
 */
function goToPage(page) {
    viewTransition(() => {
        // check if the dialog should be modal and adjust the modal status
        if (template.dataset.layout === Layout.Default) {
            const shouldBeModal = isMobile() && pagesWithMobileBackdrop.includes(page);
            const isModal = elements.dialog.dataset.modal === 'true';

            if (shouldBeModal && !isModal) {
                elements.dialog.close();
                elements.dialog.showModal();
            }

            if (isModal && !shouldBeModal) {
                elements.dialog.close();
                elements.dialog.show();
            }

            elements.dialog.dataset.modal = shouldBeModal.toString();
        }

        pages.info?.classList.remove(classes.pageActive);
        pages.settings.classList.remove(classes.pageActive);
        pages.purposes.classList.remove(classes.pageActive);
        page.classList.add(classes.pageActive);
        page.focus();
    });
}

/**
 * @param {Tab} tab
 */
function selectPurposeTab(tab) {
    for (const t of purposeTabs) {
        const isSelected = t === tab;
        t.panel.hidden = !isSelected;
        t.header.tabIndex = isSelected ? 0 : -1;
        t.header.ariaSelected = isSelected ? 'true' : 'false';
    }
}

function isMobile() {
    return window.innerWidth <= 600;
}

/**
 * load cookies on page load
 */
async function loadCookieScripts() {
    const gtagData = {};
    const selectedStorages = getSelectedStorages().filter(storage => !storages.loaded.has(storage));
    for (const storageKey of selectedStorages) {
        const keys = [`${storageKey}_storage`, ...storages.data.get(storageKey).additionalConsentKeys];
        for (const key of keys) {
            gtagData[key] = 'granted';
        }
    }

    gtag('consent', 'update', gtagData);

    const url = new URL(elements.form.action, location);

    const newUrl = new URL(`${url.origin}${url.pathname}`);
    if (url.searchParams.get('id')) {
        newUrl.searchParams.append('id', url.searchParams.get('id'));
    }
    newUrl.searchParams.append('type', url.searchParams.get('type'));
    newUrl.searchParams.append('tx_c3cookie_ajax[controller]', 'Ajax');
    newUrl.searchParams.append('tx_c3cookie_ajax[action]', 'cookieScriptContainer');
    newUrl.searchParams.append('cHash', url.searchParams.get('cHash'));

    const data = new FormData();
    data.set('tx_c3cookie_ajax[storages]', JSON.stringify(selectedStorages));

    const res = await fetch(newUrl, {
        method: 'POST',
        body: data
    });

    if (!res.ok) {
        return;
    }

    const temp = document.createElement('div');
    temp.innerHTML = await res.text();
    for (const script of temp.children) {
        const newScript = document.createElement('script');
        newScript.text = script.text;
        for (const attr of script.attributes) {
            newScript.setAttribute(attr.name, attr.value);
        }
        document.head.append(newScript);
    }

    for (const storage of selectedStorages) {
        storages.loaded.add(storage);
    }
}

async function submitCookieSettings() {
    const url = new URL(elements.form.action, location);
    const newUrl = new URL(`${url.origin}${url.pathname}`);
    newUrl.searchParams.set('type', '1613380');

    const values = {
        data: getCookieData()
    };
    if (navigator.language) {
        values.browser_language = navigator.language;
    }
    if (document.querySelector('#cookie-fe-language')) {
        values.website_language = document.querySelector('#cookie-fe-language').value;
    }

    const data = new FormData();
    data.set('tx_c3cookie_ajax[controller]', 'Ajax');
    data.set('tx_c3cookie_ajax[action]', 'cookieSubmit');
    data.set('tx_c3cookie_ajax[values]', JSON.stringify(values));

    await fetch(newUrl, {
        method: 'POST',
        body: data
    });
}

async function analyze() {
    const values = {};
    if (navigator.language) {
        values.browser_language = navigator.language;
    }
    if (document.querySelector('#cookie-fe-language')) {
        values.website_language = document.querySelector('#cookie-fe-language').value;
    }

    values.browser = {};
    values.browser.userAgent = ((navigator.userAgent) ? navigator.userAgent : '');
    values.browser.cookieEnabled = ((navigator.cookieEnabled) ? navigator.cookieEnabled : '');
    values.browser.maxTouchPoints = ((navigator.maxTouchPoints) ? navigator.maxTouchPoints : '');

    values.device = {};
    values.device.innerWidth = ((window.innerWidth) ? window.innerWidth : '');
    values.device.innerHeight = ((window.innerHeight) ? window.innerHeight : '');
    values.device.outerWidth = ((window.outerWidth) ? window.outerWidth : '');
    values.device.outerHeight = ((window.outerHeight) ? window.outerHeight : '');
    values.device.devicePixelRatio = ((window.devicePixelRatio) ? window.devicePixelRatio : '');

    values.info = {};
    values.info.url = ((window.location.href) ? window.location.href : '');
    values.info.referrer = ((document.referrer) ? document.referrer : '');

    values.info.hash = getCookieData()?.token ?? null;

    values.storages = getSelectedStorages();

    const url = new URL(elements.form.dataset.tracking, location);
    const newUrl = new URL(`${url.origin}${url.pathname}`);

    if (url.searchParams.get('id')) {
        newUrl.searchParams.append('id', url.searchParams.get('id'));
    }
    newUrl.searchParams.append('type', url.searchParams.get('type'));
    newUrl.searchParams.append('tx_c3cookie_ajax[controller]', 'Ajax');
    newUrl.searchParams.append('tx_c3cookie_ajax[action]', 'analyze');
    newUrl.searchParams.append('cHash', url.searchParams.get('cHash'));

    const data = new FormData();
    data.set('tx_c3cookie_ajax[values]', JSON.stringify(values));

    await fetch(newUrl, {
        method: 'POST',
        body: data
    });
}

/**
 * set checkboxes on user input
 * preselect in settings window
 */
function updateCheckboxes() {
    for (const storage of storages.all) {
        /** @type {HTMLInputElement} */
        const input = elements.form.querySelector(`input[value="${storage}"]`);
        if (input) {
            input.checked = storageIsAccepted(storage);
        }
    }
}

/**
 *
 * @returns {{token: string, storages: string[]}|null}
 */
function getCookieData() {
    const cookie = getCookie('c3kie');
    if (!cookie) {
        return null;
    }
    try {
        return JSON.parse(cookie);
    } catch (_) {
    }
    return null;
}

/**
 * which action was performed
 * @param {string[]} selectedStorages
 */
function selectStorages(selectedStorages) {
    const data = getCookieData();

    setCookie(
        'c3kie',
        JSON.stringify({
            token: data?.token ?? createRandomString(64),
            storages: selectedStorages
        }),
        '180'
    );

    for (const storage of storages.all) {
        const key = `c3kie_${storage}`;
        if (selectedStorages.includes(storage)) {
            localStorage.setItem(key, 'true');
        } else {
            localStorage.removeItem(key);
        }
    }

    window.dispatchEvent(new CustomEvent('c3cookie:storages:update'));

    updateCheckboxes();
}

/**
 * returns the selected storage keys
 * @returns {string[]}
 */
function getSelectedStorages() {
    return getCookieData()?.storages ?? [];
}

/**
 * checks if the passed storage key is selected
 * @param {string} storage
 * @returns {boolean}
 */
function storageIsAccepted(storage) {
    return getSelectedStorages().includes(storage) || storages.forced.includes(storage);
}

function checkCookieLifetime() {
    let c3kie = getCookie('c3kie');
    if (c3kie === null) {
        storages.all.forEach(storage => {
            localStorage.removeItem('c3kie_' + storage);
        });
        return false;
    }
    return true;
}

window.c3cookie = {
    getCookieData,
    selectStorages,
    getSelectedStorages,
    storageIsAccepted,
    openCookieWindow
};

export function initVideoElements() {
    /** @type {NodeListOf<HTMLButtonElement>} */
    const videoButtons = document.querySelectorAll('.c3cookie-video-preview-button');
    for (const videoButton of videoButtons) {
        videoButton.addEventListener('click', () => {
            convertVideoPreview(videoButton, true);
        });

        // automatic remove youtube-video-disclaimer if opt-in accepted
        if (storageIsAccepted('functionality')) {
            convertVideoPreview(videoButton);
        }
    }
}

/**
 * @param {HTMLButtonElement} element
 * @param {boolean} forceAutoplay
 */
function convertVideoPreview(element, forceAutoplay = false) {
    const parentElement = element.parentElement;
    const mainElement = parentElement.parentElement;

    const iframeUrl = new URL(element.dataset.iframeSrc);
    /** @type {Record<string, string>} */
    const attributes = JSON.parse(element.dataset.iframeAttributes);

    const iframe = document.createElement('iframe');

    for (const [key, value] of Object.entries(attributes)) {
        iframe.setAttribute(key, value);
    }

    if (forceAutoplay) {
        iframeUrl.searchParams.set('autoplay', '1');
    }

    iframe.src = iframeUrl.toString();

    mainElement.append(iframe);
    parentElement.remove();
}
